import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { isValidPhoneNumber } from "react-phone-number-input";
import PhoneInput from "react-phone-number-input";
import type { ShippingAddress } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckoutShippingStep } from "@/pages/checkout/CheckoutShippingStep";
import { HostedCheckoutPayment } from "@/pages/checkout/CheckoutPaymentStep";
import { OrderQuantitySelector } from "@/pages/checkout/OrderQuantitySelector";
import {
  resolveLineItemStock,
  variantIsPurchasable as isVariantInStock,
} from "@/lib/validateCheckoutInventory";
import { DeliveryTypeEnum } from "@/types/DeliveryType";
import { getContrastTextColor, checkoutStripeElementsAppearance } from "@/utils/colorUtils";
import { shippingAddressWithoutPhone } from "@/lib/shippingAddressWithoutPhone";
import { getShippingForPhysicalCart } from "@/lib/checkoutShipping";
import { normalizeCheckoutPhone } from "@/lib/normalizeCheckoutPhone";
import type {
  MeetingLeadContact,
  MeetingShopProduct,
  MeetingShopTipping,
} from "./meetingProductTypes";

type ShopStep = "cart" | "shipping" | "payment" | "confirmation";

type ConfirmedOrder = {
  products: Array<{
    _id: string;
    name?: string;
    price: number;
    pricePendingVariant?: boolean;
    selectedVariantId?: string;
    variants?: Array<{ _id: string; name: string; quantity?: number }>;
    images?: { url: string; thumbnailUrl?: string }[];
  }>;
  quantities: Record<string, number>;
  productSubtotal: number;
  shippingCents: number;
  shippingLabel: string;
  shippingProtectionCents: number;
  tipCents: number;
};

type CheckoutConfig = {
  stripe: { publishableKey: string; accountId?: string } | null;
  checkoutSettings: {
    tipping?: {
      enabled: boolean;
      amount1?: number;
      amount2?: number;
      amount3?: number;
    };
    colors?: {
      backgroundColor?: string;
      buttonColor?: string;
      accentColor?: string;
    };
    shippingProfiles?: Array<{
      name: string;
      productIds: string[];
      shippingZones: Array<{
        name: string;
        countries: string[];
        prices: Array<{ name: string; price: number }>;
      }>;
    }>;
    shippingProtection?: {
      enabled: boolean;
      price: number;
    };
  } | null;
};

function variantIsPurchasableForProduct({
  product,
  variant,
}: {
  product: MeetingShopProduct;
  variant: NonNullable<MeetingShopProduct["variants"]>[number];
}): boolean {
  return isVariantInStock({
    variant,
    inventoryQuantity: product.inventoryQuantity,
  });
}

function unitPriceCents({
  product,
  variantId,
}: {
  product: MeetingShopProduct;
  variantId?: string;
}): number {
  const vid = variantId?.trim();
  const variant =
    vid && product.variants?.find((entry) => String(entry._id) === vid);
  if (variant) {
    if (variant.price != null) return Math.round(variant.price);
    return Math.round(product.price ?? 0);
  }
  return Math.round(product.price ?? 0);
}

function resolveCartLineVariantId({
  product,
  lineVariantIds,
}: {
  product: MeetingShopProduct;
  lineVariantIds: Record<string, string>;
}): string | undefined {
  const variants = product.variants;
  if (!variants?.length) return undefined;

  const selected = lineVariantIds[product._id]?.trim();
  const selectedVariant = selected
    ? variants.find((entry) => String(entry._id) === selected)
    : undefined;
  if (selectedVariant && variantIsPurchasableForProduct({ product, variant: selectedVariant })) {
    return selected;
  }

  if (variants.length === 1) {
    const only = variants[0];
    if (variantIsPurchasableForProduct({ product, variant: only })) return String(only._id);
  }

  return undefined;
}

function contactPrefill({
  leadContact,
}: {
  leadContact?: MeetingLeadContact;
}) {
  const fromLeadEmail = leadContact?.email?.trim() ?? "";
  const fromLeadRawPhone = leadContact?.phone?.trim() ?? "";
  const fromLeadParsed = normalizeCheckoutPhone(fromLeadRawPhone);
  const fromLeadPhone = fromLeadParsed.ok
    ? (fromLeadParsed.e164 ?? "")
    : fromLeadRawPhone;

  if (fromLeadEmail || fromLeadPhone) {
    return { email: fromLeadEmail, phone: fromLeadPhone };
  }

  if (typeof sessionStorage === "undefined") {
    return { email: "", phone: "" };
  }
  const email = sessionStorage.getItem("email")?.trim() ?? "";
  const rawPhone = sessionStorage.getItem("phone")?.trim() ?? "";
  const parsed = normalizeCheckoutPhone(rawPhone);
  const phone = parsed.ok ? (parsed.e164 ?? "") : rawPhone;
  return { email, phone };
}

function effectiveQuantityMaximum({
  product,
  variantId,
}: {
  product: MeetingShopProduct;
  variantId?: string;
}): number {
  const ruleMax = product.quantityRules?.maximum ?? 10;
  const stock = resolveLineItemStock({
    product: {
      inventoryQuantity: product.inventoryQuantity,
      variants: product.variants,
    },
    variantId,
  });
  if (typeof stock === "number") {
    return Math.max(0, Math.min(ruleMax, stock));
  }
  return ruleMax;
}

function applyLivestreamCartQuantityRules({
  product,
  lineVariantIds,
}: {
  product: MeetingShopProduct;
  lineVariantIds: Record<string, string>;
}): MeetingShopProduct {
  const variantId = resolveCartLineVariantId({ product, lineVariantIds });
  return {
    ...product,
    quantityRules: {
      minimum: 0,
      maximum: effectiveQuantityMaximum({ product, variantId }),
    },
  };
}

function cartProductsForSelector({
  products,
  lineVariantIds,
}: {
  products: MeetingShopProduct[];
  lineVariantIds: Record<string, string>;
}): MeetingShopProduct[] {
  return products.map((product) => ({
    ...applyLivestreamCartQuantityRules({ product, lineVariantIds }),
    price: Math.round(product.price ?? 0),
  }));
}

export function LivestreamShopOverlay({
  open,
  onClose,
  meetingProductId,
  products,
  loading,
  shopTipping,
  leadContact,
  onSendChatMessage,
}: {
  open: boolean;
  onClose: () => void;
  meetingProductId: string;
  products: MeetingShopProduct[];
  loading: boolean;
  shopTipping?: MeetingShopTipping;
  leadContact?: MeetingLeadContact;
  onSendChatMessage?: (args: { text: string }) => void | Promise<void>;
}) {
  const [step, setStep] = useState<ShopStep>("cart");
  const [checkoutConfig, setCheckoutConfig] = useState<CheckoutConfig>({
    stripe: null,
    checkoutSettings: null,
  });
  const [loadingCheckoutConfig, setLoadingCheckoutConfig] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [lineVariantIds, setLineVariantIds] = useState<Record<string, string>>({});
  const [payVariantHighlightLines, setPayVariantHighlightLines] = useState(
    () => new Set<number>(),
  );
  const [customerEmail, setCustomerEmail] = useState(
    () => contactPrefill({ leadContact }).email,
  );
  const [customerPhone, setCustomerPhone] = useState(
    () => contactPrefill({ leadContact }).phone,
  );
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const [isAddressComplete, setIsAddressComplete] = useState(false);
  const [shippingProtectionOptIn, setShippingProtectionOptIn] = useState(true);
  const [hostedClientSecret, setHostedClientSecret] = useState<string | null>(null);
  const [hostedSessionError, setHostedSessionError] = useState<string | null>(null);
  const [hostedTipCents, setHostedTipCents] = useState(0);
  const [tipSelection, setTipSelection] = useState<{ optedIn: boolean; hasSelected: boolean }>({
    optedIn: false,
    hasSelected: true,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmedOrder, setConfirmedOrder] = useState<ConfirmedOrder | null>(null);
  const cartPaymentIntentIdRef = useRef<string | null>(null);
  const paymentSessionCartKeyRef = useRef<string | null>(null);
  const isProcessingRef = useRef(false);

  const tipOnlyMode = products.length === 0 && shopTipping?.enabled === true;
  const hasShopContent = products.length > 0 || shopTipping?.enabled === true;

  const effectiveCheckoutSettings = useMemo(() => {
    const base = checkoutConfig.checkoutSettings;
    if (!shopTipping) return base;
    return {
      ...(base ?? {}),
      tipping: {
        ...(base?.tipping ?? { enabled: false }),
        ...shopTipping,
      },
      colors: base?.colors,
      shippingProfiles: base?.shippingProfiles,
      shippingProtection: base?.shippingProtection,
    };
  }, [checkoutConfig.checkoutSettings, shopTipping]);

  const tipDescription = effectiveCheckoutSettings?.tipping?.description?.trim();

  const tippingEnabled = effectiveCheckoutSettings?.tipping?.enabled === true;

  const stripePromise = useMemo(() => {
    const publishableKey = checkoutConfig.stripe?.publishableKey?.trim();
    if (!publishableKey) return null;
    const options: { stripeAccount?: string } = {};
    if (checkoutConfig.stripe?.accountId) {
      options.stripeAccount = checkoutConfig.stripe.accountId;
    }
    return loadStripe(publishableKey, options);
  }, [checkoutConfig.stripe]);

  const productSignature = useMemo(
    () => products.map((product) => String(product._id)).join("|"),
    [products],
  );

  useEffect(() => {
    const initialQuantities: Record<string, number> = {};
    products.forEach((product) => {
      initialQuantities[product._id] = 0;
    });
    setQuantities(initialQuantities);
    setLineVariantIds({});
    setPayVariantHighlightLines(new Set());
  }, [productSignature]);

  useEffect(() => {
    if (!open || !leadContact) return;

    setCustomerEmail((prev) => {
      if (prev.trim()) return prev;
      return leadContact.email?.trim() ?? "";
    });
    setCustomerPhone((prev) => {
      if (prev.trim()) return prev;
      const raw = leadContact.phone?.trim() ?? "";
      if (!raw) return prev;
      const parsed = normalizeCheckoutPhone(raw);
      return parsed.ok ? (parsed.e164 ?? "") : raw;
    });
  }, [open, leadContact?.email, leadContact?.phone]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoadingCheckoutConfig(true);
    void (async () => {
      try {
        const response = await fetch("/api/checkout-config");
        if (!response.ok) return;
        const data = (await response.json()) as CheckoutConfig;
        if (!cancelled) {
          setCheckoutConfig({
            stripe: data.stripe ?? null,
            checkoutSettings: data.checkoutSettings ?? null,
          });
        }
      } finally {
        if (!cancelled) setLoadingCheckoutConfig(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    // Auto-resolve a sole variant for price/controls, but keep qty at 0 so tip-only
    // checkout remains possible when tipping is enabled.
    setLineVariantIds(() => {
      const next: Record<string, string> = {};
      products.forEach((product) => {
        const variants = product.variants;
        if (!variants?.length) return;
        if (variants.length === 1) {
          const only = variants[0];
          if (variantIsPurchasableForProduct({ product, variant: only })) {
            next[product._id] = String(only._id);
          }
        }
      });
      return next;
    });
    setPayVariantHighlightLines(new Set());
  }, [productSignature, products]);

  const selectorProducts = useMemo(() => {
    return cartProductsForSelector({ products, lineVariantIds }).map((product) => {
      const variantId = resolveCartLineVariantId({ product, lineVariantIds });
      const variant = variantId
        ? product.variants?.find((entry) => String(entry._id) === variantId)
        : undefined;
      const variantResolved = !product.variants?.length || (!!variantId && !!variant);
      const displayIndex = products.findIndex((entry) => entry._id === product._id);
      return {
        ...product,
        price: variantResolved ? unitPriceCents({ product, variantId }) : 0,
        pricePendingVariant: !!product.variants?.length && !variantResolved,
        selectedVariantId: variantId,
        variantSelectNeedsAttention: payVariantHighlightLines.has(displayIndex),
      };
    });
  }, [products, lineVariantIds, payVariantHighlightLines]);

  useEffect(() => {
    setQuantities((prev) => {
      let changed = false;
      const next = { ...prev };
      products.forEach((product) => {
        const variantId = resolveCartLineVariantId({ product, lineVariantIds });
        const max = effectiveQuantityMaximum({ product, variantId });
        const qty = prev[product._id] ?? 0;
        if (qty > max) {
          next[product._id] = max;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [lineVariantIds, productSignature, products]);

  const cartProducts = useMemo(
    () =>
      products
        .map((product, index) => ({ product, index }))
        .filter(({ product }) => (quantities[product._id] ?? 0) > 0),
    [products, quantities],
  );

  const tipTitle =
    cartProducts.length > 0
      ? effectiveCheckoutSettings?.tipping?.title?.trim() || "Add tip"
      : "No merch, send a tip instead";

  const lineDisplay = useMemo(() => {
    return cartProducts.map(({ product }) => {
      const variants = product.variants;
      const variantId = resolveCartLineVariantId({
        product,
        lineVariantIds,
      });
      const variant = variantId
        ? product.variants?.find((entry) => String(entry._id) === variantId)
        : undefined;
      const variantResolved = !variants?.length || (!!variantId && !!variant);
      const unitPrice = variantResolved
        ? unitPriceCents({ product, variantId })
        : 0;
      return {
        variantId: variant && variantId ? variantId : undefined,
        variantResolved,
        unitPrice,
        baseName: product.name || "Product",
      };
    });
  }, [cartProducts, lineVariantIds]);

  const displayProducts = useMemo(
    () =>
      cartProducts.map(({ product, index }, displayIndex) => ({
        ...applyLivestreamCartQuantityRules({ product, lineVariantIds }),
        price: lineDisplay[displayIndex].unitPrice,
        pricePendingVariant:
          !!product.variants?.length && !lineDisplay[displayIndex].variantResolved,
        name: lineDisplay[displayIndex].baseName,
        selectedVariantId: lineDisplay[displayIndex].variantId,
        variantSelectNeedsAttention: payVariantHighlightLines.has(displayIndex),
      })),
    [cartProducts, lineDisplay, lineVariantIds, payVariantHighlightLines],
  );

  const variantSelectionsComplete = useMemo(() => {
    return selectorProducts.every((product) => {
      const qty = quantities[product._id] ?? 0;
      if (qty <= 0) return true;
      return !product.pricePendingVariant;
    });
  }, [selectorProducts, quantities]);

  const getCartItems = useCallback(() => {
    return cartProducts.map(({ product }, displayIndex) => ({
      product: displayProducts[displayIndex],
      quantity: quantities[product._id] ?? 0,
      variantId: lineDisplay[displayIndex].variantId,
    }));
  }, [cartProducts, displayProducts, quantities, lineDisplay]);

  const needsShipping = useMemo(() => {
    return getCartItems().some(
      (item) => item.product.deliveryType !== DeliveryTypeEnum.DIGITAL,
    );
  }, [getCartItems]);

  const shippingQuotePhysical = useMemo(() => {
    if (!needsShipping) return { cents: 0, label: "" };
    const physicalProductIds = [
      ...new Set(
        getCartItems().flatMap((item) =>
          item.product.deliveryType === DeliveryTypeEnum.DIGITAL ? [] : [item.product._id],
        ),
      ),
    ];
    return getShippingForPhysicalCart({
      physicalProductIds,
      shippingProfiles: checkoutConfig.checkoutSettings?.shippingProfiles ?? null,
      shipToCountryCode: shippingAddress?.address?.country?.trim() || null,
    });
  }, [
    needsShipping,
    getCartItems,
    checkoutConfig.checkoutSettings?.shippingProfiles,
    shippingAddress?.address?.country,
  ]);

  const shippingProtectionUnitCents = Math.max(
    0,
    Math.round(checkoutConfig.checkoutSettings?.shippingProtection?.price ?? 0),
  );
  const shippingProtectionSelectable =
    needsShipping &&
    checkoutConfig.checkoutSettings?.shippingProtection?.enabled === true &&
    shippingProtectionUnitCents > 0;

  const getShippingProtectionCents = () =>
    shippingProtectionSelectable && shippingProtectionOptIn
      ? shippingProtectionUnitCents
      : 0;

  const getProductSubtotal = useCallback(() => {
    return getCartItems().reduce((sum, item) => {
      const product = item.product as { price?: number; pricePendingVariant?: boolean };
      if (product.pricePendingVariant) return sum;
      return sum + (product.price || 0) * item.quantity;
    }, 0);
  }, [getCartItems]);

  const getEffectiveTotal = useCallback(() => {
    return (
      getProductSubtotal() +
      shippingQuotePhysical.cents +
      getShippingProtectionCents()
    );
  }, [getProductSubtotal, shippingQuotePhysical.cents, shippingProtectionOptIn, shippingProtectionSelectable, shippingProtectionUnitCents]);

  const sessionCartKey = useMemo(
    () =>
      JSON.stringify({
        items: getCartItems().map((item) => ({
          productId: item.product._id,
          quantity: item.quantity,
          variantId: item.variantId,
        })),
        total: getEffectiveTotal(),
        hostedTipCents,
        shippingAddress,
        customerEmail,
      }),
    [getCartItems, getEffectiveTotal, hostedTipCents, shippingAddress, customerEmail],
  );

  const sessionCartKeyRef = useRef(sessionCartKey);
  sessionCartKeyRef.current = sessionCartKey;

  const handleQuantityChange = (productId: string, quantity: number) => {
    const product = products.find((entry) => entry._id === productId);
    const variantId = product
      ? resolveCartLineVariantId({ product, lineVariantIds })
      : undefined;
    const max = product
      ? effectiveQuantityMaximum({ product, variantId })
      : 10;
    const clamped = Math.max(0, Math.min(max, quantity));
    setQuantities((prev) => ({ ...prev, [productId]: clamped }));
  };

  const handleLineVariantChange = ({
    displayIndex,
    variantId,
    productList,
  }: {
    displayIndex: number;
    variantId: string;
    productList: Array<{ _id: string }>;
  }) => {
    const product = productList[displayIndex];
    if (!product) return;
    const trimmedVariantId = variantId.trim();
    setLineVariantIds((prev) => {
      if (!trimmedVariantId) {
        const next = { ...prev };
        delete next[product._id];
        return next;
      }
      return { ...prev, [product._id]: trimmedVariantId };
    });
    // Selecting a variant adds the product; clearing it returns to tip-only-friendly qty 0.
    setQuantities((prev) => ({
      ...prev,
      [product._id]: trimmedVariantId ? 1 : 0,
    }));
    setPayVariantHighlightLines((prev) => {
      const next = new Set(prev);
      next.delete(displayIndex);
      return next;
    });
    setError(null);
  };

  const selectorSubtotal = useMemo(() => {
    return selectorProducts.reduce((sum, product) => {
      const qty = quantities[product._id] ?? 0;
      if (qty <= 0 || product.pricePendingVariant) return sum;
      return sum + (product.price || 0) * qty;
    }, 0);
  }, [selectorProducts, quantities]);

  const contactComplete =
    customerEmail.trim() !== "" && isValidPhoneNumber(customerPhone);

  const validateVariantsForPayment = useCallback(() => {
    const missing: number[] = [];
    selectorProducts.forEach((product, displayIndex) => {
      const qty = quantities[product._id] ?? 0;
      if (qty <= 0 || !product.variants?.length) return;
      if (product.pricePendingVariant) missing.push(displayIndex);
    });
    if (missing.length > 0) {
      setPayVariantHighlightLines(new Set(missing));
      return {
        ok: false as const,
        message: "Please choose an option for each product before placing your order.",
      };
    }
    return { ok: true as const };
  }, [selectorProducts, quantities]);

  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  const resetCart = () => {
    const initialQuantities: Record<string, number> = {};
    products.forEach((product) => {
      initialQuantities[product._id] = 0;
    });
    setQuantities(initialQuantities);
    setLineVariantIds({});
    setPayVariantHighlightLines(new Set());
  };

  const handleClose = () => {
    if (step === "confirmation") {
      resetCart();
    }
    setStep("cart");
    setError(null);
    setConfirmedOrder(null);
    setHostedClientSecret(null);
    setHostedSessionError(null);
    setHostedTipCents(0);
    cartPaymentIntentIdRef.current = null;
    paymentSessionCartKeyRef.current = null;
    onClose();
  };

  const handleReturnToLivestream = () => {
    resetCart();
    handleClose();
  };

  const handleContinue = () => {
    setError(null);
    if (step === "cart") {
      const hasItems = getCartItems().length > 0;
      const tipOnlyCheckout = tippingEnabled && hostedTipCents > 0 && !hasItems;
      if (!hasItems && !tipOnlyCheckout) {
        setError(
          tippingEnabled
            ? "Select a tip amount or at least one product to continue."
            : "Select at least one product to continue.",
        );
        return;
      }
      // Tip-only checkout has no product lines; skip variant gating.
      if (hasItems) {
        const variantCheck = validateVariantsForPayment();
        if (!variantCheck.ok) {
          setError(variantCheck.message ?? "Please choose an option for each product.");
          return;
        }
      }
      if (!contactComplete) {
        setError("Enter a valid email and phone number to continue.");
        return;
      }
      setStep(needsShipping ? "shipping" : "payment");
      return;
    }
    if (step === "shipping") {
      setStep("payment");
    }
  };

  useEffect(() => {
    if (step !== "payment" || !open) {
      paymentSessionCartKeyRef.current = null;
      setHostedClientSecret(null);
      setHostedSessionError(null);
      cartPaymentIntentIdRef.current = null;
      return;
    }
    if (!stripePromise) return;

    const isTipSelectionPending =
      checkoutConfig.checkoutSettings?.tipping?.enabled === true &&
      tipSelection.optedIn &&
      !tipSelection.hasSelected;

    if (isTipSelectionPending) {
      paymentSessionCartKeyRef.current = null;
      setHostedClientSecret(null);
      setHostedSessionError(null);
      cartPaymentIntentIdRef.current = null;
      return;
    }

    if (!contactComplete || !variantSelectionsComplete) {
      paymentSessionCartKeyRef.current = null;
      setHostedClientSecret(null);
      setHostedSessionError(null);
      cartPaymentIntentIdRef.current = null;
      return;
    }
    if (isProcessingRef.current) return;
    if (paymentSessionCartKeyRef.current === sessionCartKey) return;

    const fetchForKey = sessionCartKey;
    let cancelled = false;

    void (async () => {
      setHostedSessionError(null);
      try {
        const items = getCartItems();
        const response = await fetch("/api/checkout/cart-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            products: items.map((item) => ({
              productId: item.product._id,
              quantity: item.quantity,
              ...(item.variantId ? { variantId: item.variantId } : {}),
            })),
            total: getEffectiveTotal(),
            tipCents: hostedTipCents,
            shipping: shippingAddress
              ? shippingAddressWithoutPhone(shippingAddress)
              : undefined,
            shippingProtection:
              shippingProtectionSelectable && shippingProtectionOptIn,
          }),
        });
        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
          clientSecret?: string;
          paymentIntentId?: string;
        };
        if (!response.ok) {
          throw new Error(data.error || "Could not prepare payment");
        }
        if (!data.clientSecret) {
          throw new Error("Missing payment session");
        }
        if (!cancelled && sessionCartKeyRef.current === fetchForKey) {
          paymentSessionCartKeyRef.current = fetchForKey;
          setHostedClientSecret(data.clientSecret);
          cartPaymentIntentIdRef.current =
            typeof data.paymentIntentId === "string" &&
            data.paymentIntentId.trim() !== ""
              ? data.paymentIntentId.trim()
              : null;
        }
      } catch (err) {
        if (!cancelled) {
          paymentSessionCartKeyRef.current = null;
          const message =
            err instanceof Error ? err.message : "Could not prepare payment";
          setHostedSessionError(message);
          setHostedClientSecret(null);
          setError(message);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    step,
    open,
    stripePromise,
    sessionCartKey,
    contactComplete,
    variantSelectionsComplete,
    getCartItems,
    getEffectiveTotal,
    hostedTipCents,
    shippingAddress,
    shippingProtectionOptIn,
    shippingProtectionSelectable,
    checkoutConfig.checkoutSettings?.tipping?.enabled,
    tipSelection,
  ]);

  const submitCartOrder = async ({
    tipAmountCents,
    paymentIntentId,
  }: {
    tipAmountCents?: number;
    paymentIntentId?: string;
  }) => {
    const paymentIntent =
      paymentIntentId?.trim() || cartPaymentIntentIdRef.current?.trim() || "";
    if (!paymentIntent) {
      throw new Error("Payment not completed");
    }
    if (!customerEmail.trim()) {
      throw new Error("Email is required");
    }
    if (!isValidPhoneNumber(customerPhone)) {
      throw new Error("Valid phone number is required");
    }

    const response = await fetch("/api/checkout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        paymentIntentId: paymentIntent,
        meetingProductId,
        products: getCartItems().map((item) => ({
          productId: item.product._id,
          quantity: item.quantity,
          ...(item.variantId ? { variantId: item.variantId } : {}),
        })),
        email: customerEmail,
        phone: customerPhone,
        shipping: shippingAddress
          ? shippingAddressWithoutPhone(shippingAddress)
          : undefined,
        total: getEffectiveTotal(),
        shippingProtection:
          shippingProtectionSelectable && shippingProtectionOptIn,
        ...(typeof tipAmountCents === "number" && tipAmountCents > 0
          ? { tipAmount: tipAmountCents }
          : {}),
      }),
    });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      throw new Error(errorData.error || "Payment failed");
    }

    const data = (await response.json()) as { goTo?: string };
    const goTo =
      typeof data.goTo === "string" && data.goTo.trim() !== ""
        ? data.goTo.trim()
        : null;

    for (const product of displayProducts) {
      const productName = product.name?.trim() || "Product";
      const variant = product.selectedVariantId
        ? product.variants?.find(
            (entry) => String(entry._id) === product.selectedVariantId,
          )
        : undefined;
      const variantName = variant?.name?.trim();
      const displayName = variantName
        ? `${productName} (${variantName})`
        : productName;
      void onSendChatMessage?.({ text: `Purchased ${displayName}` });
    }

    if (goTo) {
      handleClose();
      window.location.assign(goTo);
      return;
    }

    setConfirmedOrder({
      products: displayProducts.map((product) => ({
        _id: product._id,
        name: product.name,
        price: product.price,
        pricePendingVariant: product.pricePendingVariant,
        selectedVariantId: product.selectedVariantId,
        variants: product.variants,
        images: product.images,
      })),
      quantities: Object.fromEntries(
        displayProducts.map((product) => [product._id, quantities[product._id] ?? 0]),
      ),
      productSubtotal: getProductSubtotal(),
      shippingCents: shippingQuotePhysical.cents,
      shippingLabel: shippingQuotePhysical.label,
      shippingProtectionCents: getShippingProtectionCents(),
      tipCents:
        typeof tipAmountCents === "number" && tipAmountCents > 0
          ? tipAmountCents
          : hostedTipCents,
    });
    setIsProcessing(false);
    setError(null);
    setHostedClientSecret(null);
    setHostedSessionError(null);
    cartPaymentIntentIdRef.current = null;
    paymentSessionCartKeyRef.current = null;
    setStep("confirmation");
  };

  const canProceed = () => {
    if (step === "cart") {
      const hasItems = getCartItems().length > 0;
      const tipOnlyCheckout = tippingEnabled && hostedTipCents > 0 && !hasItems;
      if (tipOnlyCheckout || tipOnlyMode) {
        return contactComplete && hostedTipCents > 0;
      }
      return (
        hasItems &&
        variantSelectionsComplete &&
        contactComplete
      );
    }
    if (step === "shipping") {
      return isAddressComplete && shippingAddress !== null;
    }
    return false;
  };

  const totalSteps = tipOnlyMode ? 2 : needsShipping ? 3 : 2;
  const currentStepNumber =
    step === "cart" ? 1 : step === "shipping" ? 2 : totalSteps;

  const backgroundColor =
    checkoutConfig.checkoutSettings?.colors?.backgroundColor || "#ffffff";
  const buttonColor =
    checkoutConfig.checkoutSettings?.colors?.buttonColor || "#000000";
  const accentColor =
    checkoutConfig.checkoutSettings?.colors?.accentColor || "#000000";
  const textColor = getContrastTextColor(backgroundColor);
  const accentTextColor = getContrastTextColor(accentColor);
  const buttonTextColor = getContrastTextColor(buttonColor);

  const checkoutAppearance = useMemo(
    () =>
      checkoutStripeElementsAppearance({
        backgroundColor,
        accentColor: checkoutConfig.checkoutSettings?.colors?.accentColor,
      }),
    [backgroundColor, checkoutConfig.checkoutSettings?.colors?.accentColor],
  );

  const paymentQuantities = useMemo(
    () =>
      Object.fromEntries(
        displayProducts.map((product) => [product._id, quantities[product._id] ?? 0]),
      ),
    [displayProducts, quantities],
  );

  const handleHostedTipAmountCentsChange = useCallback(
    ({ tipAmountCents }: { tipAmountCents: number }) => {
      setHostedTipCents((prev) => (prev === tipAmountCents ? prev : tipAmountCents));
    },
    [],
  );

  const persistContactToSessionStorage = () => {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem("email", customerEmail.trim());
    sessionStorage.setItem("phone", customerPhone.trim());
  };

  const renderContactFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="livestream-shop-email">Email *</Label>
        <Input
          id="livestream-shop-email"
          type="email"
          placeholder="Enter your email"
          value={customerEmail}
          onChange={(event) => setCustomerEmail(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="livestream-shop-phone">Phone *</Label>
        <PhoneInput
          id="livestream-shop-phone"
          defaultCountry="US"
          international={false}
          value={customerPhone || undefined}
          onChange={(value) => setCustomerPhone(value || "")}
          autoComplete="tel"
          className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
        />
      </div>
    </>
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/60 backdrop-blur-sm">
      <div className="relative flex shrink-0 items-center justify-center px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
        <h2 className="text-lg font-semibold text-center text-white">
          {step === "confirmation" ? "Order confirmed" : "Shop"}
        </h2>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          onClick={handleClose}
          className="absolute right-3 h-10 w-10 text-white hover:bg-white/10 hover:text-white sm:right-4"
          aria-label="Close shop"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2 sm:px-6">
        <div
          className="mx-auto flex w-full max-w-2xl flex-col gap-4 rounded-2xl px-4 pb-5 pt-4 sm:px-6"
          style={{ backgroundColor, color: textColor }}
        >
          {step !== "confirmation" ? (
          <div className="flex items-center justify-center gap-0 py-2">
            {Array.from({ length: totalSteps }, (_, index) => index + 1).map(
              (stepNumber, index) => (
                <div key={stepNumber} className="flex items-center">
                  {index > 0 ? (
                    <div
                      className="h-px w-8 shrink-0 mx-1"
                      style={{ backgroundColor: accentColor, opacity: 0.4 }}
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      if (isProcessing || stepNumber >= currentStepNumber) return;
                      if (stepNumber === 1) setStep("cart");
                      else if (stepNumber === 2 && needsShipping) setStep("shipping");
                    }}
                    disabled={isProcessing || stepNumber > currentStepNumber}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer disabled:cursor-not-allowed"
                    style={
                      stepNumber === currentStepNumber
                        ? { backgroundColor: accentColor, color: accentTextColor }
                        : stepNumber < currentStepNumber
                          ? {
                              border: "1px solid",
                              borderColor: accentColor,
                              backgroundColor: "transparent",
                              color: textColor,
                            }
                          : {
                              border: "1px solid rgba(128,128,128,0.3)",
                              backgroundColor: "rgba(128,128,128,0.2)",
                              color: "rgba(128,128,128,0.6)",
                            }
                    }
                  >
                    {stepNumber}
                  </button>
                </div>
              ),
            )}
          </div>
          ) : null}

          {loading || loadingCheckoutConfig ? (
            <p className="text-sm opacity-70">Loading…</p>
          ) : !hasShopContent ? (
            <p className="text-sm opacity-70">No products available.</p>
          ) : !checkoutConfig.stripe?.publishableKey ? (
            <p className="text-sm opacity-70">Checkout is not available right now.</p>
          ) : step === "confirmation" && confirmedOrder ? (
            <div className="space-y-4 flex-1">
              <p className="text-center text-sm opacity-80">
                Thank you! Your order has been placed.
              </p>
              <OrderQuantitySelector
                products={confirmedOrder.products}
                quantities={confirmedOrder.quantities}
                onQuantityChange={() => {}}
                total={confirmedOrder.productSubtotal}
                shippingCents={confirmedOrder.shippingCents}
                shippingLabel={confirmedOrder.shippingLabel}
                shippingProtectionCents={confirmedOrder.shippingProtectionCents}
                shippingProtectionSelectable={confirmedOrder.shippingProtectionCents > 0}
                shippingProtectionChecked={confirmedOrder.shippingProtectionCents > 0}
                tipAmount={confirmedOrder.tipCents}
                showTipLineItem={confirmedOrder.tipCents > 0}
                readOnly
                checkoutColors={checkoutConfig.checkoutSettings?.colors ?? null}
                showShippingLineItem={confirmedOrder.shippingCents > 0}
                showShippingProtectionLineItem={
                  confirmedOrder.shippingProtectionCents > 0
                }
              />
              <div className="flex justify-center pt-2">
                <Button
                  type="button"
                  onClick={handleReturnToLivestream}
                  style={{
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                  }}
                  className="font-bold hover:opacity-90"
                >
                  Return to livestream
                </Button>
              </div>
            </div>
          ) : (
            <>
              {step === "cart" ? (
                <div className="space-y-4 flex-1">
                  {!tipOnlyMode ? (
                    <OrderQuantitySelector
                      products={selectorProducts}
                      quantities={quantities}
                      onQuantityChange={handleQuantityChange}
                      onLineVariantChange={({ displayIndex, variantId }) => {
                        handleLineVariantChange({
                          displayIndex,
                          variantId,
                          productList: selectorProducts,
                        });
                      }}
                      total={selectorSubtotal}
                      checkoutColors={checkoutConfig.checkoutSettings?.colors ?? null}
                      showTipSelector={tippingEnabled}
                      tipping={effectiveCheckoutSettings?.tipping}
                      tipTitle={tipTitle}
                      tipDescription={tipDescription}
                      tipAmount={hostedTipCents}
                      showTipLineItem={hostedTipCents > 0}
                      onTipCentsChange={handleHostedTipAmountCentsChange}
                      idPrefix="livestream-shop"
                    />
                  ) : null}
                  {tippingEnabled && tipOnlyMode ? (
                    <OrderQuantitySelector
                      products={[]}
                      quantities={{}}
                      onQuantityChange={() => {}}
                      total={0}
                      checkoutColors={checkoutConfig.checkoutSettings?.colors ?? null}
                      showTipSelector
                      tipping={effectiveCheckoutSettings?.tipping}
                      tipTitle={tipTitle}
                      tipDescription={tipDescription}
                      tipAmount={hostedTipCents}
                      showTipLineItem={hostedTipCents > 0}
                      onTipCentsChange={handleHostedTipAmountCentsChange}
                      idPrefix="livestream-shop"
                    />
                  ) : null}
                  {renderContactFields()}
                </div>
              ) : null}

              {step === "shipping" && needsShipping && stripePromise ? (
                <div className="space-y-4 flex-1">
                  <CheckoutShippingStep
                    products={displayProducts}
                    quantities={Object.fromEntries(
                      displayProducts.map((product) => [
                        product._id,
                        quantities[product._id] ?? 0,
                      ]),
                    )}
                    onQuantityChange={handleQuantityChange}
                    onLineVariantChange={({ displayIndex, variantId }) => {
                      handleLineVariantChange({
                        displayIndex,
                        variantId,
                        productList: displayProducts,
                      });
                    }}
                    shippingAddress={shippingAddress}
                    onShippingAddressChange={setShippingAddress}
                    onAddressCompleteChange={setIsAddressComplete}
                    stripePromise={stripePromise}
                    total={getProductSubtotal()}
                    shippingCents={shippingQuotePhysical.cents}
                    shippingLabel={shippingQuotePhysical.label}
                    shippingProtectionCents={getShippingProtectionCents()}
                    shippingProtectionSelectable={shippingProtectionSelectable}
                    shippingProtectionUnitCents={shippingProtectionUnitCents}
                    shippingProtectionChecked={shippingProtectionOptIn}
                    onShippingProtectionChange={setShippingProtectionOptIn}
                    checkoutSettings={effectiveCheckoutSettings}
                  />
                </div>
              ) : null}

              {step === "payment" && stripePromise ? (
                <div className="space-y-4 flex-1">
                  {contactComplete ? (
                    <HostedCheckoutPayment
                    stripePromise={stripePromise}
                    clientSecret={hostedClientSecret}
                    sessionError={hostedSessionError}
                    variantSelectionsComplete={variantSelectionsComplete}
                    validateVariantsForPayment={validateVariantsForPayment}
                    elementsKey={hostedClientSecret ?? "pending"}
                    appearance={checkoutAppearance}
                    products={displayProducts}
                    quantities={paymentQuantities}
                    onQuantityChange={handleQuantityChange}
                    onLineVariantChange={({ displayIndex, variantId }) => {
                      handleLineVariantChange({
                        displayIndex,
                        variantId,
                        productList: displayProducts,
                      });
                    }}
                    total={getProductSubtotal()}
                    shippingCents={shippingQuotePhysical.cents}
                    shippingLabel={shippingQuotePhysical.label}
                    shippingProtectionCents={getShippingProtectionCents()}
                    shippingProtectionSelectable={shippingProtectionSelectable}
                    shippingProtectionUnitCents={shippingProtectionUnitCents}
                    shippingProtectionChecked={shippingProtectionOptIn}
                    onShippingProtectionChange={setShippingProtectionOptIn}
                    name={shippingAddress?.name ?? ""}
                    email={customerEmail}
                    hideTipSelector
                    tipCents={hostedTipCents}
                    onHostedTipAmountCentsChange={handleHostedTipAmountCentsChange}
                    onTipSelectionChange={setTipSelection}
                    onFinalizeHostedCheckout={submitCartOrder}
                    isProcessing={isProcessing}
                    error={error}
                    setError={setError}
                    setIsProcessing={setIsProcessing}
                    checkoutSettings={effectiveCheckoutSettings}
                  />
                  ) : null}
                </div>
              ) : null}

              {step !== "payment" ? (
                <div className="flex flex-col items-end gap-2 pt-2 mt-auto">
                  {error ? (
                    <p className="text-sm text-red-500 text-right w-full">{error}</p>
                  ) : null}
                  <Button
                    type="button"
                    onClick={() => {
                      persistContactToSessionStorage();
                      handleContinue();
                    }}
                    disabled={!canProceed() || isProcessing}
                    style={{
                      backgroundColor: buttonColor,
                      color: buttonTextColor,
                    }}
                    className="font-bold hover:opacity-90"
                  >
                    {step === "cart"
                      ? tipOnlyMode ||
                        (tippingEnabled &&
                          hostedTipCents > 0 &&
                          getCartItems().length === 0)
                        ? "Continue to Payment"
                        : "Continue to Checkout"
                      : "Continue"}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
